/**
 *
 * QuestionnaireEdit
 *
 */

import React, { memo, FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';
import { Wrap } from '../../../../assets/styledAssets/grid';
import {
  accentColorSecond,
  fontColorSecond,
} from '../../../../assets/styledAssets/vars';
import Button from '../../../../components/Button/Button.component';
import { BackIcon } from '../../../../components/Icons/Icons.component';
import {
  StyledDropDownList,
  StyledFormField,
  StyledLabel,
} from '../../../../components/FormField/styles';
import {StyledA, StyledAppBar, StyledTabs} from './styles';
import { useRouteMatch } from 'react-router';
import QuestionnarieService from '../../../../service/Questionnarie.service';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../../../app/store';
import {
  setActiveQuestionnaire,
  editSection,
  editQuestion,
  editAnswer,
  addQuestion,
  addAnswer,
  removeSection,
  removeAnswer,
  removeQuestion,
  copySection,
  copyQuestion,
  setSeriesToDelete,
} from '../../Questionnaires.slice';
import { Language, QuestionSeries } from '../../questionnaires';
import { StyledLoader } from '../../../../assets/styledAssets/loader';
import { addSection } from '../../Questionnaires.actions';
import { cloneDeep } from 'lodash';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import QuestionnaireEditQuestion from "../QuestionnaireEditQuestion/QuestionnaireEditQuestion.component"
import { Tab } from "@material-ui/core";
import QuestionnaireEditCode from '../QuestionnaireEditCode/QuestionnaireEditCode.component'
import QuestionnairesForm from '../QuestionnairesForm/QuestionnairesForm.component'

interface MatchParams {
  questionId: string;
  languageId: string;
}

const connector = connect(
  (state: RootState) => ({
    activeQuestionnaire: state.questionnaires.activeQuestionnaire,
    seriesToDelete: state.questionnaires.seriesToDelete,
    profile: state.app.user,
  }),
  {
    setActiveQuestionnaire,
    editSection,
    editQuestion,
    editAnswer,
    addSection,
    addQuestion,
    addAnswer,
    removeSection,
    removeAnswer,
    removeQuestion,
    copySection,
    copyQuestion,
    setSeriesToDelete,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{overflowY: 'auto', height: 'calc(100% - 5rem)', paddingTop: '2rem', width:'100%'}}
    >
      {value === index && (
        <Wrap>
          {children}
        </Wrap>
      )}
    </div>
  );
}


const QuestionnaireEdit: FunctionComponent<PropsFromRedux> = ({
  profile,
  activeQuestionnaire,
  seriesToDelete,
  setActiveQuestionnaire,
  editSection,
  editQuestion,
  editAnswer,
  addSection,
  addQuestion,
  addAnswer,
  removeSection,
  removeAnswer,
  removeQuestion,
  copySection,
  copyQuestion,
  setSeriesToDelete,
}) => {
  const intl = useIntl();
  const [activeSeries, setActiveSeries] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const { params } = useRouteMatch<MatchParams>();
  const [publishedStatus] = useState([
    { value: true, label: intl.formatMessage(messages.published) },
    { value: false, label: intl.formatMessage(messages.unpublished) },
  ]);
  const [status, setStatus] = useState<{ value: boolean; label: string }>();
  const [listLangs, setListLangs] = useState<Language[]>([]);
  const [selectedLanguge, setSelectedLanguge] = useState<Language>();
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    setShowLoader(true);
    setSeriesToDelete([]);
    QuestionnarieService.fetchQuestionnaire(
      params.questionId,
      params.languageId,
    )
      .then((res) => {
        setActiveQuestionnaire(res);
        setStatus(publishedStatus.find((v) => v.value === res?.isPublished));
        if (!res.questionSeries.length) {
          addSection({ seriesIdx: 0 });
        }
      })
      .finally(() => setShowLoader(false));
    QuestionnarieService.fetchListLanguges().then((res) => {
      setListLangs(res);
      setSelectedLanguge(res.find((v) => v.id === params.languageId));
    });
    return () => {
      setActiveQuestionnaire(undefined);
    };
    // eslint-disable-next-line
  }, []);

  const handleLangChange = (e: DropDownListChangeEvent) => {
    setSelectedLanguge(e.value);
    setShowLoader(true);
    setSeriesToDelete([]);
    QuestionnarieService.fetchQuestionnaire(
      activeQuestionnaire?.id as string,
      e.value.id,
    )
      .then((res) => {
        setActiveQuestionnaire(res);
        setStatus(publishedStatus.find((v) => v.value === res?.isPublished));
        if (!res.questionSeries.length) {
          addSection({ seriesIdx: 0 });
        }
      })
      .finally(() => setShowLoader(false));
  };

  const saveSeries = () => {
    if (activeQuestionnaire) {
      setShowLoader(true);
      const listRequest: Promise<QuestionSeries>[] = [];
      activeQuestionnaire.questionSeries.forEach((questionSeries) => {
        const clonedSeries = cloneDeep(questionSeries);
        clonedSeries.personId = profile?.id as string;
        listRequest.push(
          QuestionnarieService.createQuestionSeries(
            params.questionId,
            selectedLanguge?.id as string,
            clonedSeries,
          ),
        );
      });
      Promise.all(listRequest)
        .then((res) => {
          setActiveQuestionnaire({
            ...activeQuestionnaire,
            questionSeries: res,
          });

          seriesToDelete.forEach((id) => {
            QuestionnarieService.deleteQuestionSeries(id).then(() => {});
          });
          setSeriesToDelete([]);

          if (activeQuestionnaire.isPublished !== status?.value) {
            QuestionnarieService.toggleQuestionStatus(
              activeQuestionnaire?.id as string,
            ).then(() => {});
          }
        })
        .finally(() => setShowLoader(false));

      const copyQ = Object.assign({}, activeQuestionnaire);
      copyQ.languageId = selectedLanguge?.id;
      copyQ.personId = profile?.id as string;
      QuestionnarieService.createQuestionnaire(copyQ).then(() => {});
    }
  };

  return (
    <Wrap
      flexWrap={'nowrap'}
      flexDir={'column'}
      width={'100%'}
      overflow={'auto'}
      style={{ position: 'relative' }}
    >
      <Wrap
        alignItems={'flex-end'}
        width={'100%'}
        justifyCont={'space-between'}
        margin={'0 0 3rem 0'}
      >
        <Wrap flexDir={'column'} flexWrap={'nowrap'} maxWidth={'40rem'}>
          <Wrap
            fs={'1.4rem'}
            color={fontColorSecond}
            alignItems={'flex-end'}
            padding={'0 0 .5rem 4rem'}
          >
            {intl.formatMessage(messages.editQuestionnaire)}
          </Wrap>
          <Wrap margin={'0 1rem 0 -1rem'} alignItems={'center'}>
            <Button
              icon
              to={'/questionnaires'}
              children={<BackIcon color={'#000'} width={'2rem'} />}
            />
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
            >
              {activeQuestionnaire?.name}
            </Wrap>
          </Wrap>
        </Wrap>
        <Button primary onClick={saveSeries}>
          {intl.formatMessage(messages.save)}
        </Button>
      </Wrap>
      <Wrap
        maxHeight={'calc(100% - 10rem)'}
        width={'100%'}
        maxWidth={'110rem'}
        overflow={'hidden'}
        justifyCont={'space-between'}
      >
        <Wrap
          flexDir={'column'}
          flexWrap={'nowrap'}
          width={'25rem'}
          overflow={'auto'}
          height={'100%'}
        >
          <Wrap
            width={'100%'}
            margin={'0 0 2rem 0'}
            flexDir={'column'}
            flexWrap={'nowrap'}
          >
            <StyledLabel>{intl.formatMessage(messages.language)}</StyledLabel>
            <StyledDropDownList
              data={listLangs}
              textField="name"
              dataItemKey={'id'}
              value={selectedLanguge}
              onChange={handleLangChange}
            />
          </Wrap>
          <Wrap
            width={'100%'}
            margin={'0 0 2rem 0'}
            flexDir={'column'}
            flexWrap={'nowrap'}
          >
            <StyledLabel>{intl.formatMessage(messages.status)}</StyledLabel>
            <StyledDropDownList
              value={status}
              onChange={(e) => setStatus(e.value)}
              data={publishedStatus}
              textField={'label'}
              dataItemKey={'value'}
            />
          </Wrap>
          {activeQuestionnaire && (
            <Wrap
              width={'100%'}
              margin={'0 0 4rem 0'}
              padding={'0 0 3rem 0'}
              borders={[{ side: 'bottom', color: accentColorSecond }]}
              flexDir={'column'}
              flexWrap={'nowrap'}
            >
              <StyledLabel>
                {intl.formatMessage(messages.description)}
              </StyledLabel>
              <StyledFormField>
                <textarea
                  onChange={(e) =>
                    setActiveQuestionnaire({
                      ...activeQuestionnaire,
                      description: e.target.value as string,
                    })
                  }
                  value={activeQuestionnaire?.description || ''}
                />
              </StyledFormField>
            </Wrap>
          )}
          <Wrap fw={'bold'} fs={'3rem'} margin={'0 0 3rem 0'} color={'#000'}>
            {intl.formatMessage(messages.questions)}
          </Wrap>
          {activeQuestionnaire?.questionSeries.map((series, index) => (
            <Wrap
              key={`quest-${index}`}
              flexDir={'column'}
              flexWrap={'nowrap'}
              margin={'0 0 3rem 0'}
            >
              <Wrap color={fontColorSecond} fw={'bold'} margin={'0 0 1rem 0'}>
                {intl.formatMessage(messages.section)} {index + 1}{' '}
                {intl.formatMessage(messages.of)}{' '}
                {activeQuestionnaire?.questionSeries.length}
              </Wrap>
              <StyledA
                className={activeSeries === index ? 'active' : ''}
                onClick={() => setActiveSeries(index)}
                href={`#section-${index}`}
              >
                {series.name}
              </StyledA>
            </Wrap>
          ))}
        </Wrap>
        <Wrap width={'calc(100% - 27rem)'} height={'100%'} overflow={'hidden'}>
          <Wrap width={'100%'} justifyCont={'center'} borders={[{side: 'bottom', color: accentColorSecond}]}>
            <StyledAppBar position="static">
              <StyledTabs value={value} onChange={handleChange} aria-label="simple tabs example">
                <Tab label={intl.formatMessage(messages.questions)} />
                <Tab label={intl.formatMessage(messages.code)} />
                <Tab label={intl.formatMessage(messages.preview)} />
              </StyledTabs>
            </StyledAppBar>
          </Wrap>
          <TabPanel value={value} index={0}>
            <QuestionnaireEditQuestion
              activeQuestionnaire={activeQuestionnaire}
              editSection={editSection}
              editQuestion={editQuestion}
              editAnswer={editAnswer}
              addSection={addSection}
              addQuestion={addQuestion}
              addAnswer={addAnswer}
              removeSection={removeSection}
              removeAnswer={removeAnswer}
              removeQuestion={removeQuestion}
              copySection={copySection}
              copyQuestion={copyQuestion}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <QuestionnaireEditCode activeQuestionnaire={activeQuestionnaire} />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <QuestionnairesForm  activeQuestionnaire={activeQuestionnaire}/>
          </TabPanel>
        </Wrap>
      </Wrap>
      {showLoader && <StyledLoader />}
    </Wrap>
  );
};

export default connector(memo(QuestionnaireEdit));
